import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { filter, Observable } from 'rxjs';
import { ShipmentListState } from './shipment-list.state';
import {
  ShipmentListInitialState,
  ShipmentListLoad,
  ShipmentStatisticsLoad,
  UpdateDeliveryNoteAlertList,
  UpdateLoadAlertList,
  UpdateTransportOrderAlertList,
  UpdateWaybillAlertList,
} from './shipment-list.actions';
import { LoadDataFacade, SetInitialStateFacade } from '../../../../../common/models';
import { Shipment, ShipmentListDto } from '../models/shipment-list/shipment-list.model';
import { ShipmentListSearchCriteria } from '../models/shipment.model';
import { ShipmentQuickFilterModel, ShipmentAlertPayload } from '../../models/shipment-common-models';
import { ListSearchCriteriaFacade, ListSearchCriteriaType } from '../../../list-search-criteria';
import { MatDialogRef } from '@angular/material/dialog';
import { map } from 'rxjs/operators';
import { isDeliveryNoteParams, isLoadIdView, isTransportOrderIdView, isWaybillIdView } from '../../../../../common';

@Injectable({ providedIn: 'root' })
export class ShipmentListFacade implements SetInitialStateFacade, LoadDataFacade {
  listDto$: Observable<ShipmentListDto> = this.store.select(ShipmentListState.getListDto);
  items$: Observable<Shipment[]> = this.store.select(ShipmentListState.getItems);
  loaded$: Observable<boolean> = this.store.select(ShipmentListState.getLoaded);
  statisticLoaded$: Observable<boolean> = this.store.select(ShipmentListState.getStatisticLoaded);
  infoStatistics$: Observable<ShipmentQuickFilterModel[]> = this.store.select(ShipmentListState.getInfoStatistics);
  milestoneStatistics$: Observable<ShipmentQuickFilterModel[]> = this.store.select(
    ShipmentListState.getMilestoneStatistics,
  );
  deviationStatistics$: Observable<ShipmentQuickFilterModel[]> = this.store.select(
    ShipmentListState.getDeviationStatistics,
  );
  totalElements$: Observable<number> = this.store.select(ShipmentListState.getTotalElements);
  mapThreshold$: Observable<number> = this.store.select(ShipmentListState.getMapThreshold);
  isMapThresholdReached$: Observable<boolean> = this.loaded$.pipe(
    filter(Boolean),
    map(() => this.store.selectSnapshot(ShipmentListState.getIsMapThresholdReached)),
  );

  constructor(private store: Store, private listSearchCriteriaFacade: ListSearchCriteriaFacade) {}

  loadAll(): void {
    this.listSearchCriteriaFacade
      .getSearchCriteriaByListType(ListSearchCriteriaType.Shipment)
      .subscribe((searchCriteria: ShipmentListSearchCriteria) => {
        {
          this.store.dispatch(new ShipmentListLoad({ searchCriteria }));
        }
      });
  }

  loadStatistics(): void {
    this.listSearchCriteriaFacade
      .getSearchCriteriaByListType(ListSearchCriteriaType.Shipment)
      .subscribe((searchCriteria: ShipmentListSearchCriteria) => {
        this.store.dispatch(new ShipmentStatisticsLoad({ searchCriteria }));
      });
  }

  updateAlertList({ idView, events }: ShipmentAlertPayload, dialogRef: MatDialogRef<unknown>): void {
    if (isWaybillIdView(idView)) {
      this.store.dispatch(
        new UpdateWaybillAlertList({
          shipmentWaybillAlertUpdatePayload: {
            waybillIdView: idView,
            events,
          },
          dialogRef,
        }),
      );
    } else if (isLoadIdView(idView)) {
      this.store.dispatch(
        new UpdateLoadAlertList({ shipmentLoadAlertUpdatePayload: { ...idView, events }, dialogRef }),
      );
    } else if (isTransportOrderIdView(idView)) {
      this.store.dispatch(
        new UpdateTransportOrderAlertList({
          shipmentTransportOrderAlertUpdatePayload: { ...idView, events },
          dialogRef,
        }),
      );
    } else if (isDeliveryNoteParams(idView)) {
      this.store.dispatch(
        new UpdateDeliveryNoteAlertList({
          shipmentDeliveryNoteAlertUpdatePayload: { ...idView, events },
          dialogRef,
        }),
      );
    }
  }

  setInitialState(): void {
    this.store.dispatch(new ShipmentListInitialState());
  }
}
